import * as d3 from 'd3';
import { defineTexturedGlassPatterns } from './../glassPatternsDefs';

// Function to draw octagons with frames
// The octagon fits within a rectangle of width w and height h
// It has 8 sides: top, bottom, left, right, and 4 corner chamfers
export function shapeOctagon(svg, d, x, y, octagons, labelTextOrOptions = null, subFrame = false, fullCorners = false, halfCorners = false,
  displayLabel = true, labelOptions = {}, drawFrame = true) {

  // Support both old positional style and new object style
  let labelText, options;
  if (typeof labelTextOrOptions === 'object' && labelTextOrOptions !== null && !Array.isArray(labelTextOrOptions)) {
    // New style: options object passed
    options = labelTextOrOptions;
    labelText = options.labelText ?? null;
    subFrame = options.subFrame ?? false;
    fullCorners = options.fullCorners ?? false;
    halfCorners = options.halfCorners ?? false;
    displayLabel = options.displayLabel ?? true;
    labelOptions = options.labelOptions ?? {};
    drawFrame = options.drawFrame ?? true;
  } else {
    // Old style: positional parameters
    labelText = labelTextOrOptions;
  }

  let currentX = x;
  let prev = {};
  let innerPoints; // Declare outside forEach to make it accessible for return

  const {
    fontSize = 14,
    fontColor = 'black',
    fontFamily = 'Arial',
    fontOpacity = 0.4,
  } = labelOptions;

  octagons.forEach((octagon, index) => {
    const {
      w, // width of bounding rectangle
      h = prev.h ?? 100, // height of bounding rectangle
      chamfer = prev.chamfer ?? Math.min(w, h) / 4, // size of corner chamfers (default: 1/4 of smaller dimension)
      color = prev.color ?? 'white',
      glassType = prev.glassType || null,
      glassDesign = prev.glassDesign || null,
      frameWidth = prev.frameWidth ?? 0,
      frameColor = prev.frameColor ?? 'white',
      lineFrameColor = prev.lineFrameColor ?? frameColor,
      lineFrameThickness = prev.lineFrameThickness ?? 1,
      outerStrokeColor = prev.outerStrokeColor ?? 'black',
      outerStrokeWidth = prev.outerStrokeWidth ?? 1,
      forceSubFrame = prev.forceSubFrame || false,
      fullCorners = prev.fullCorners || false,
      halfCorners = prev.halfCorners || false,
      displayLabel = prev.displayLabel ?? true,
      middleFrame = prev.middleFrame ?? true  // Default true to show middle frame
    } = octagon;

    let baseFill = color;
    let tintOverlay = null;

    // TINTED GLASS
    if (glassType === 'Tinted') {
      baseFill = 'white';
      switch (glassDesign?.toLowerCase()) {
        case 'grey':
          tintOverlay = 'rgba(60, 60, 60, 0.2)';
          break;
        case 'bronze':
          tintOverlay = 'rgba(90, 50, 0, 0.2)';
          break;
        default:
          console.warn('Unknown tinted glass design:', glassDesign);
      }
    }

    // TEXTURED GLASS
    if (glassType === 'Textured' && glassDesign) {
      const patternId = `${glassDesign.toLowerCase().replace(/\s/g, '')}Pattern`;
      tintOverlay = `url(#${patternId})`;
      defineTexturedGlassPatterns(svg, glassDesign);
    }

    // Define octagon points
    // Regular octagon with chamfered corners fitting in rectangle w x h
    // Points go clockwise starting from top-left chamfer
    let outerPoints;

    // Outer octagon (with frame extension)
    const c = chamfer; // chamfer size

    // 8 points of octagon (clockwise from top-left):
    // Top-left corner chamfer
    const P1 = [currentX + c - frameWidth, y - frameWidth];
    // Top-right corner chamfer
    const P2 = [currentX + w - c + frameWidth, y - frameWidth];
    // Right side top
    const P3 = [currentX + w + frameWidth, y + c - frameWidth];
    // Right side bottom
    const P4 = [currentX + w + frameWidth, y + h - c + frameWidth];
    // Bottom-right corner chamfer
    const P5 = [currentX + w - c + frameWidth, y + h + frameWidth];
    // Bottom-left corner chamfer
    const P6 = [currentX + c - frameWidth, y + h + frameWidth];
    // Left side bottom
    const P7 = [currentX - frameWidth, y + h - c + frameWidth];
    // Left side top
    const P8 = [currentX - frameWidth, y + c - frameWidth];

    outerPoints = [P1, P2, P3, P4, P5, P6, P7, P8];

    // Helper: offset a line segment inward by distance d
    function offsetLine(P, Q, d) {
      const dx = Q[0] - P[0];
      const dy = Q[1] - P[1];
      const len = Math.hypot(dx, dy);

      // inward normal (perpendicular to the left)
      const nx = -dy / len;
      const ny = dx / len;

      return [
        [P[0] + nx * d, P[1] + ny * d],
        [Q[0] + nx * d, Q[1] + ny * d]
      ];
    }

    // Helper: intersection of two infinite lines
    function intersect(L1, L2) {
      const [A, B] = L1;
      const [C, D] = L2;

      const a1 = B[1] - A[1];
      const b1 = A[0] - B[0];
      const c1 = a1 * A[0] + b1 * A[1];

      const a2 = D[1] - C[1];
      const b2 = C[0] - D[0];
      const c2 = a2 * C[0] + b2 * C[1];

      const det = a1 * b2 - a2 * b1;

      return [
        (b2 * c1 - b1 * c2) / det,
        (a1 * c2 - a2 * c1) / det
      ];
    }

    // Offset each edge inward by frameWidth
    const E1 = offsetLine(P1, P2, frameWidth); // top edge
    const E2 = offsetLine(P2, P3, frameWidth); // top-right chamfer
    const E3 = offsetLine(P3, P4, frameWidth); // right edge
    const E4 = offsetLine(P4, P5, frameWidth); // bottom-right chamfer
    const E5 = offsetLine(P5, P6, frameWidth); // bottom edge
    const E6 = offsetLine(P6, P7, frameWidth); // bottom-left chamfer
    const E7 = offsetLine(P7, P8, frameWidth); // left edge
    const E8 = offsetLine(P8, P1, frameWidth); // top-left chamfer

    // Intersect offset edges to get inner octagon
    const P1_inner = intersect(E8, E1);
    const P2_inner = intersect(E1, E2);
    const P3_inner = intersect(E2, E3);
    const P4_inner = intersect(E3, E4);
    const P5_inner = intersect(E4, E5);
    const P6_inner = intersect(E5, E6);
    const P7_inner = intersect(E6, E7);
    const P8_inner = intersect(E7, E8);

    innerPoints = [P1_inner, P2_inner, P3_inner, P4_inner, P5_inner, P6_inner, P7_inner, P8_inner];

    // === OUTER FRAME ===
    if (drawFrame && frameWidth > 0) {
      const outerPointsStr = outerPoints.map(p => p.join(',')).join(' ');
      svg.append('polygon')
        .attr('points', outerPointsStr)
        .attr('fill', frameColor)
        .attr('stroke', lineFrameColor)
        .attr('stroke-width', lineFrameThickness);
    }

    // === BASE FILL ===
    const innerPointsStr = innerPoints.map(p => p.join(',')).join(' ');
    svg.append('polygon')
      .attr('points', innerPointsStr)
      .attr('fill', baseFill)
      .attr('stroke', outerStrokeColor)
      .attr('stroke-width', outerStrokeWidth);

    // === TINT OVERLAY ===
    if (tintOverlay) {
      svg.append('polygon')
        .attr('points', innerPointsStr)
        .attr('fill', tintOverlay)
        .attr('stroke', 'none');
    }

    // === SUBFRAME (middle frame between outer frame and glass) ===
    if (drawFrame && ((subFrame === true && index === 0) || (forceSubFrame === true)) && middleFrame) {
      const subInset = frameWidth / 2; // distance from outer frame to middle frame

      // Calculate middle frame octagon points by offsetting from outer points
      const [P1, P2, P3, P4, P5, P6, P7, P8] = outerPoints;

      function offsetLine(P, Q, d) {
        const dx = Q[0] - P[0];
        const dy = Q[1] - P[1];
        const len = Math.hypot(dx, dy);
        const nx = -dy / len;
        const ny = dx / len;
        return [
          [P[0] + nx * d, P[1] + ny * d],
          [Q[0] + nx * d, Q[1] + ny * d]
        ];
      }

      function intersect(L1, L2) {
        const [A, B] = L1;
        const [C, D] = L2;
        const a1 = B[1] - A[1];
        const b1 = A[0] - B[0];
        const c1 = a1 * A[0] + b1 * A[1];
        const a2 = D[1] - C[1];
        const b2 = C[0] - D[0];
        const c2 = a2 * C[0] + b2 * C[1];
        const det = a1 * b2 - a2 * b1;
        return [
          (b2 * c1 - b1 * c2) / det,
          (a1 * c2 - a2 * c1) / det
        ];
      }

      // Offset each edge inward by subInset
      const M1 = offsetLine(P1, P2, subInset);
      const M2 = offsetLine(P2, P3, subInset);
      const M3 = offsetLine(P3, P4, subInset);
      const M4 = offsetLine(P4, P5, subInset);
      const M5 = offsetLine(P5, P6, subInset);
      const M6 = offsetLine(P6, P7, subInset);
      const M7 = offsetLine(P7, P8, subInset);
      const M8 = offsetLine(P8, P1, subInset);

      // Intersect offset edges to get middle octagon
      const P1_middle = intersect(M8, M1);
      const P2_middle = intersect(M1, M2);
      const P3_middle = intersect(M2, M3);
      const P4_middle = intersect(M3, M4);
      const P5_middle = intersect(M4, M5);
      const P6_middle = intersect(M5, M6);
      const P7_middle = intersect(M6, M7);
      const P8_middle = intersect(M7, M8);

      const middlePoints = [P1_middle, P2_middle, P3_middle, P4_middle, P5_middle, P6_middle, P7_middle, P8_middle];
      const middlePointsStr = middlePoints.map(p => p.join(',')).join(' ');

      svg.append('polygon')
        .attr('points', middlePointsStr)
        .attr('fill', 'none')
        .attr('stroke', 'black')
        .attr('stroke-width', 0.5);
    }

    // === LABEL ===
    const textToUse = labelText == null ? index + 1 : labelText;

    if (displayLabel) {
      // Position label at center of octagon
      const centroidX = currentX + w / 2;
      const centroidY = y + h / 2;

      svg.append('text')
        .attr('x', centroidX)
        .attr('y', centroidY + fontSize / 3)
        .attr('text-anchor', 'middle')
        .attr('dominant-baseline', 'middle')
        .attr('font-size', fontSize)
        .attr('fill', fontColor)
        .attr('fill-opacity', fontOpacity)
        .attr('font-family', fontFamily)
        .text(textToUse);
    }

    currentX += w + 2 * frameWidth + d + 2 * lineFrameThickness;

    prev = {
      w, h, chamfer, color,
      frameWidth, frameColor,
      lineFrameColor, lineFrameThickness,
      outerStrokeColor, outerStrokeWidth,
      fullCorners, halfCorners, displayLabel,
      glassType, glassDesign, middleFrame
    };
  });

  return innerPoints;
}
